import * as React from 'react';
import { useState, useCallback } from 'react';
import styled from 'styled-components';

import HighlightingRulesContext from 'views/components/contexts/HighlightingRulesContext';
import { DocumentTitle, Spinner } from 'components/common';
import PageContentLayout from 'components/layout/PageContentLayout';
import useSearchConfiguration from 'hooks/useSearchConfiguration';
import useActiveSearch from 'data-lake/preview/hooks/useActiveSearch';
import DataLakePreviewStoreProvider from 'data-lake/preview/store/DataLakePreviewStoreProvider';
import useUrlParams from 'data-lake/preview/hooks/useUrlParams';
import useCurrentUser from 'hooks/useCurrentUser';
import { isPermitted } from 'util/PermissionsMixin';
import Panel from 'components/bootstrap/Panel';
import { Link } from 'components/common/router';
import DataLakeRoutes from 'data-lake/Routes';
import useHasActiveBackend from 'data-lake/preview/hooks/useHasActiveBackend';
import useLicenseValidityForSubject from 'license/hooks/useLicenseValidityForSubject';
import { LICENSE_SUBJECTS } from 'license/constants';
import { InvalidLicenseWarning } from 'license/LicenseCheck';
import type HighlightingRule from 'views/logic/views/formatting/highlighting/HighlightingRule';
import Sidebar from 'views/components/sidebar/Sidebar';
import DataLakeHighlightingRules from 'data-lake/preview/DataLakeHighlightingRules';
import useActiveDataLakes from 'data-lake/preview/hooks/useActiveDataLakes';
import type { ActivePreviewSearch, DataLakeURLQueryParams, DataLakeInfoArray } from 'data-lake/preview/Types';
import ConfiguredDataLakesProvider from 'data-lake/preview/contexts/ConfiguredDataLakesProvider';
import useConfiguredDataLakes from 'data-lake/preview/hooks/useConfiguredDataLakes';
import type Search from 'views/logic/search/Search';
import usePluginEntities from 'hooks/usePluginEntities';
import FieldTypesProvider from 'data-lake/preview/contexts/FieldTypesProvider';
import type { QueryString } from 'views/logic/queries/types';

const initialSearchForDataLake = (dataLakeQueryType: QueryString['type'], activeSearch: Search) => {
  const queryType = activeSearch?.queries?.first().query.type;

  if (queryType === dataLakeQueryType) {
    return activeSearch;
  }

  return undefined;
};

type PreviewPageContentProps = {
  activeSearch: Search | undefined;
  searchJob: { status: ActivePreviewSearch['status']; id: string; nodeId: string; progress: number } | undefined;
  initialUrlParams: DataLakeURLQueryParams;
  resetUrlQueryParams: () => void;
};

const PreviewPageContent = ({
  activeSearch,
  initialUrlParams,
  searchJob,
  resetUrlQueryParams,
}: PreviewPageContentProps) => {
  const { selectedDataLake } = useConfiguredDataLakes();
  const dataLakePlugins = usePluginEntities('dataLakeStorageTypes');
  const dataLakePlugin = dataLakePlugins.find(({ storageType }) => storageType === selectedDataLake.storageType);
  const PageComponent = dataLakePlugin.previewComponent;
  const initialSearch = initialSearchForDataLake(dataLakePlugin.queryType, activeSearch);

  if (!PageComponent) {
    throw new Error(`No page component found for data lake storage type: ${selectedDataLake.storageType}`);
  }

  return (
    <PageComponent
      initialSearch={initialSearch}
      initialUrlParams={initialUrlParams}
      initialSearchJob={searchJob}
      resetUrlQueryParams={resetUrlQueryParams}
    />
  );
};

const StyledPageContentLayout = styled(PageContentLayout)`
  .page-content-grid {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    overflow: auto;
  }
`;

const GridContainer = styled.div`
  display: flex;
  height: 100%;
`;

const DataLakePreviewPageComponent = ({ activeDataLakes }: { activeDataLakes: DataLakeInfoArray }) => {
  const { initialUrlParams, resetUrlQueryParams } = useUrlParams();
  const { config } = useSearchConfiguration();
  const [highlightingRules, setHighlightingRules] = useState<Array<HighlightingRule>>([]);
  const highlightSidebarSection = useCallback(
    () => <DataLakeHighlightingRules setHighlightingRules={setHighlightingRules} />,
    [],
  );
  const {
    search: activeSearch,
    result: activeSearchResult,
    searchJob,
    isLoading: isLoadingActiveSearch,
  } = useActiveSearch(initialUrlParams);

  return (
    <DocumentTitle title="Data Lake Preview">
      {config && !isLoadingActiveSearch ? (
        <ConfiguredDataLakesProvider
          initialUrlParams={initialUrlParams}
          activeSearch={activeSearch}
          searchJobStatus={searchJob.status}
          activeDataLakes={activeDataLakes}>
          <DataLakePreviewStoreProvider result={activeSearchResult}>
            <FieldTypesProvider>
              <HighlightingRulesContext.Provider value={highlightingRules}>
                <GridContainer>
                  <Sidebar
                    sections={[
                      {
                        key: 'highlighting',
                        icon: 'format_paragraph' as const,
                        title: 'Highlighting',
                        content: highlightSidebarSection,
                      },
                    ]}
                    enableSidebarPinning={false}
                    actions={[]}
                    title="Data Lake Preview"
                  />
                  <StyledPageContentLayout>
                    <PreviewPageContent
                      activeSearch={activeSearch}
                      initialUrlParams={initialUrlParams}
                      searchJob={searchJob}
                      resetUrlQueryParams={resetUrlQueryParams}
                    />
                  </StyledPageContentLayout>
                </GridContainer>
              </HighlightingRulesContext.Provider>
            </FieldTypesProvider>
          </DataLakePreviewStoreProvider>
        </ConfiguredDataLakesProvider>
      ) : (
        <Spinner />
      )}
    </DocumentTitle>
  );
};

const LicenseCheck = ({ children }: React.PropsWithChildren) => {
  const {
    data: { valid: isValidLicense },
    isInitialLoading: isLoadingLicense,
  } = useLicenseValidityForSubject(LICENSE_SUBJECTS.enterprise);

  if (isLoadingLicense) {
    return <Spinner />;
  }

  if (!isValidLicense) {
    return (
      <PageContentLayout>
        <InvalidLicenseWarning
          title="Data Lake Preview is disabled"
          featureName="Data Lake Preview"
          licenseSubject={LICENSE_SUBJECTS.enterprise}
          displayWarningContainer
          bsStyle="danger"
        />
      </PageContentLayout>
    );
  }

  return children;
};

type Props = {
  children: (props: { activeDataLakes: DataLakeInfoArray }) => React.ReactNode;
};

const ActiveBackendCheck = ({ children }: Props) => {
  const currentUser = useCurrentUser();
  // Todo: Later we can replace useHasActiveBackend with useActiveDataLakes. useActiveDataLakes also provides `hasActiveDataLake`.
  const { data: activeDataLakes, isInitialLoading: isInitialLoadingActiveDataLakes } = useActiveDataLakes();
  const { data: hasActiveBackend, isInitialLoading: isLoadingConfig } = useHasActiveBackend();
  const hasConfigPermissions = isPermitted(currentUser.permissions, 'data_lake_config:update');

  if (isLoadingConfig || isInitialLoadingActiveDataLakes) {
    return <Spinner />;
  }

  if (!hasActiveBackend) {
    return (
      <PageContentLayout>
        <Panel bsStyle="info">
          <Panel.Heading>There is no active Data Lake Backend</Panel.Heading>
          <Panel.Body>
            To be able open the <b>Data Lake Preview Page</b> you need to configure and activate a{' '}
            {hasConfigPermissions ? (
              <Link to={DataLakeRoutes.BACKEND}>Data Lake Backend</Link>
            ) : (
              <>
                Data Lake Backend. <span>To set up a Data Lake contact your Administrator.</span>
              </>
            )}
          </Panel.Body>
        </Panel>
      </PageContentLayout>
    );
  }

  return children({ activeDataLakes });
};

const DataLakePreviewPage = () => (
  <LicenseCheck>
    <ActiveBackendCheck>
      {({ activeDataLakes }) => <DataLakePreviewPageComponent activeDataLakes={activeDataLakes} />}
    </ActiveBackendCheck>
  </LicenseCheck>
);

export default DataLakePreviewPage;
